import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Copy, ExternalLink } from "lucide-react";
import { Blog, ContentPageTheme } from "@/services/bedrock";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { EditableBlog } from "./BlogModal/BlogEditor";
import { BlogPreviewAuthor, toBlogPreviewPayload } from "./blogPreviewPayload";

export type BlogPreviewPart = "title" | "subtitle" | "tags" | "takeaways";

export type BlogPreviewHandle = {
  highlightSection: (index: number | null) => void;
  clearFocusedBlock: () => void;
  scrollToSection: (index: number, blockIndex?: number) => void;
  followSection: (index: number) => void;
  unfollowSection: () => void;
  scrollToPart: (part: BlogPreviewPart) => void;
  highlightPart: (part: BlogPreviewPart | null) => void;
};

interface BlogPreviewProps {
  blog: Blog;
  draft: EditableBlog;
  theme?: ContentPageTheme | null;
  author?: BlogPreviewAuthor;
  shareUrl?: string;
  onCopyShareUrl?: () => void;
  onOpenPage?: () => void;
  className?: string;
}

export const BlogPreview = forwardRef<BlogPreviewHandle, BlogPreviewProps>(
  ({ blog, draft, theme, author, shareUrl, onCopyShareUrl, onOpenPage, className }, ref) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const followIndexRef = useRef<number | null>(null);
    const [isIFrameConnected, setIsIFrameConnected] = useState(0);

    // Keep the iframe on the same protocol as this page. An HTTPS app cannot load http://localhost:4321.
    const iframeOrigin = useMemo(() => {
      if (window.location.hostname === "localhost") {
        return `${window.location.protocol}//localhost:4321`;
      }
      return window.location.origin;
    }, []);

    const previewUrl = `${iframeOrigin}/b/preview`;
    const payload = useMemo(() => toBlogPreviewPayload(blog, draft, theme, author), [blog, draft, theme, author]);

    const postToPreview = useCallback(
      (message: Record<string, unknown>) => {
        if (!isIFrameConnected || !iframeRef.current?.contentWindow) return;
        iframeRef.current.contentWindow.postMessage(message, iframeOrigin);
      },
      [isIFrameConnected, iframeOrigin]
    );

    const sendPayload = useCallback(() => {
      postToPreview({ type: "BLOG", payload, followSectionIndex: followIndexRef.current });
    }, [postToPreview, payload]);

    useImperativeHandle(
      ref,
      () => ({
        highlightSection: (index: number | null) => {
          postToPreview({ type: "BLOG_SECTION", action: index == null ? "unhover" : "highlight", sectionIndex: index });
        },
        clearFocusedBlock: () => {
          postToPreview({ type: "BLOG_SECTION", action: "clearFocus" });
        },
        scrollToSection: (index: number, blockIndex?: number) => {
          postToPreview({ type: "BLOG_SECTION", action: "scroll", sectionIndex: index, blockIndex });
        },
        followSection: (index: number) => {
          followIndexRef.current = index;
          postToPreview({ type: "BLOG_SECTION", action: "follow", sectionIndex: index });
        },
        unfollowSection: () => {
          followIndexRef.current = null;
          postToPreview({ type: "BLOG_SECTION", action: "unfollow" });
        },
        scrollToPart: (part: BlogPreviewPart) => {
          followIndexRef.current = null;
          postToPreview({ type: "BLOG_SECTION", action: "scroll", part });
        },
        highlightPart: (part: BlogPreviewPart | null) => {
          postToPreview({ type: "BLOG_SECTION", action: part == null ? "clear" : "highlight", part });
        },
      }),
      [postToPreview]
    );

    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== iframeOrigin) return;
        setIsIFrameConnected(count => count + 1);
      };

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [iframeOrigin]);

    useEffect(() => {
      sendPayload();
    }, [sendPayload]);

    return (
      <div
        className={cn(
          "flex flex-col overflow-hidden rounded-2xl bg-slate-900 shadow-xl shadow-slate-900/15",
          className
        )}
      >
        <div className="flex items-center gap-2 px-3 py-2.5">
          <span className="size-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">Live preview</span>
          <div className="flex-grow" />
          {shareUrl && onCopyShareUrl && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 text-white/70 hover:bg-white/10 hover:text-white"
              aria-label="Copy share link"
              onClick={onCopyShareUrl}
            >
              <Copy className="size-3.5" />
            </Button>
          )}
          {onOpenPage && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 text-white/70 hover:bg-white/10 hover:text-white"
              aria-label="Open blog page"
              onClick={onOpenPage}
            >
              <ExternalLink className="size-3.5" />
            </Button>
          )}
        </div>
        <iframe
          ref={iframeRef}
          src={previewUrl}
          title="Blog preview"
          className="min-h-0 w-full flex-1 rounded-t-lg bg-white overscroll-contain"
          onLoad={() => setIsIFrameConnected(count => count + 1)}
        />
      </div>
    );
  }
);

BlogPreview.displayName = "BlogPreview";
